import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';

import { LINK_PHONE_TO_EXISTING_ACCOUNT_MUTATION } from '@/auth/store/queries';

import type { EmailConflictStepProps, LinkPhoneToExistingAccountResponse } from '../../types';
import { getErrorMessage } from '../../utils/errorHandling';
import { useStyles } from './EmailConflictStep.styles';

export function EmailConflictStep({
  email,
  phone,
  onLinkSuccess,
  onUseDifferentEmail,
  onForgotPassword,
  isLoading: externalLoading,
  error: externalError,
}: EmailConflictStepProps): JSX.Element {
  const s = useStyles();

  // Local state
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Link phone mutation
  const [linkPhone, { loading: linkLoading }] = useMutation<LinkPhoneToExistingAccountResponse>(
    LINK_PHONE_TO_EXISTING_ACCOUNT_MUTATION
  );

  const isLoading = externalLoading || linkLoading;
  const error = externalError || localError;

  // Handle login to link phone
  const handleLogin = useCallback(async () => {
    if (!password || isLoading) return;

    setLocalError(null);

    try {
      const { data } = await linkPhone({
        variables: {
          email,
          password,
          phone,
        },
      });

      if (data?.linkPhoneToExistingAccount?.success) {
        onLinkSuccess(data.linkPhoneToExistingAccount);
      } else {
        setLocalError('Unable to link phone number. Please try again.');
      }
    } catch (err) {
      setLocalError(getErrorMessage(err));
    }
  }, [email, password, phone, isLoading, linkPhone, onLinkSuccess]);

  // Handle form submit (Enter key)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && password) {
        handleLogin();
      }
    },
    [password, handleLogin]
  );

  return (
    <>
      <div className={s.formContent}>
        {/* Warning icon */}
        <div className={s.iconContainer}>
          <svg
            className={s.icon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Title and subtitle */}
        <h3 className={s.title}>Account Found</h3>
        <p className={s.subtitle}>
          An account with this email already exists. Log in to link your phone number.
        </p>

        {/* Error message */}
        {error && (
          <div className={s.errorMessage}>
            <p className={s.errorText}>{error}</p>
          </div>
        )}

        {/* Email (readonly) */}
        <div className={s.inputGroup}>
          <label htmlFor="conflict-email" className={s.label}>
            Email
          </label>
          <input
            id="conflict-email"
            type="email"
            className={s.input}
            value={email}
            readOnly
            disabled
          />
        </div>

        {/* Password */}
        <div className={s.inputGroup}>
          <label htmlFor="conflict-password" className={s.label}>
            Password
          </label>
          <input
            id="conflict-password"
            type="password"
            className={`${s.input} ${error ? s.inputError : ''}`}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>

        {/* Forgot password link */}
        <div className={s.forgotPasswordLink}>
          <button
            type="button"
            className={s.forgotPasswordButton}
            onClick={onForgotPassword}
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>
      </div>

      {/* Footer with buttons */}
      <div className={s.footer}>
        <button
          type="button"
          className={s.differentEmailButton}
          onClick={onUseDifferentEmail}
          disabled={isLoading}
        >
          Use Different Email
        </button>
        <button
          type="button"
          className={s.loginButton}
          onClick={handleLogin}
          disabled={!password || isLoading}
        >
          {isLoading ? 'Logging in...' : 'Log In'}
        </button>
      </div>
    </>
  );
}

export default EmailConflictStep;
